import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import TaxSuggestionsPanel from '../components/TaxSuggestionsPanel';
import VerificationPanel from '../components/VerificationPanel';
import TrustFilieBadge from '../components/shared/TrustFilieBadge';
import { RootState } from '../store';
import { setFullReturnAnalysis, setAnalysisLoading, setAnalysisError, setVerificationLoading } from '../store/analysisSlice';
import { setForms, setIsLoading as setFormsLoading } from '../store/formsSlice';
import { documentAnalysisApi, taxFormsApi, extractErrorMessage } from '../services/api';
import { Download, Send, Info, CheckCircle, AlertCircle, RefreshCw, Loader, FileText, ShieldCheck, Users } from 'lucide-react';

export default function TaxReview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { documents } = useSelector((state: RootState) => state.documents);
  const { answers: questionnaireAnswers } = useSelector((state: RootState) => state.questionnaire);
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const { fullReturnAnalysis, taxSuggestions, analysisLoading, analysisError, verificationLoading } = useSelector(
    (state: RootState) => state.analysis
  );
  const { forms, isGenerating: formsGenerating } = useSelector((state: RootState) => state.forms);
  const selectedTaxYear = useSelector((state: RootState) => state.analysis.selectedTaxYear);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastLoadedTaxYearRef = useRef<number | null>(null);
  const lastLoadedProjectIdRef = useRef<number | null>(null);

  const loadTaxAnalysis = useCallback(async () => {
    try {
      dispatch(setAnalysisLoading(true));
      dispatch(setAnalysisError(null));

      const filingStatus = questionnaireAnswers.filingStatus || 'single';
      const analysisResponse = await documentAnalysisApi.analyzeFullReturn(
        selectedTaxYear,
        filingStatus,
        questionnaireAnswers,
        activeProjectId
      );
      dispatch(setFullReturnAnalysis(analysisResponse.data));

      // Load generated forms
      try {
        const formsResponse = await taxFormsApi.getLatestForms(selectedTaxYear);
        dispatch(setForms(formsResponse.data.forms || []));
      } catch {
        // Forms may not exist yet, that's OK
      }
    } catch (err) {
      dispatch(setAnalysisError(extractErrorMessage(err, 'Unable to analyze your tax return. Please try again.')));
    } finally {
      dispatch(setAnalysisLoading(false));
    }
  }, [activeProjectId, dispatch, questionnaireAnswers, selectedTaxYear]);

  useEffect(() => {
    if (!isInitialized && documents.length > 0) {
      lastLoadedTaxYearRef.current = selectedTaxYear;
      lastLoadedProjectIdRef.current = activeProjectId;
      loadTaxAnalysis();
      setIsInitialized(true);
    }
  }, [activeProjectId, documents.length, isInitialized, loadTaxAnalysis, selectedTaxYear]);

  // Re-analyze only when the selected tax year/project scope actually changes.
  useEffect(() => {
    if (
      isInitialized &&
      documents.length > 0 &&
      (lastLoadedTaxYearRef.current !== selectedTaxYear ||
        lastLoadedProjectIdRef.current !== activeProjectId)
    ) {
      lastLoadedTaxYearRef.current = selectedTaxYear;
      lastLoadedProjectIdRef.current = activeProjectId;
      loadTaxAnalysis();
    }
  }, [activeProjectId, selectedTaxYear, isInitialized, documents.length, loadTaxAnalysis]);

  const handleRefreshAnalysis = () => {
    lastLoadedTaxYearRef.current = null;
    lastLoadedProjectIdRef.current = null;
    setIsInitialized(false);
  };

  const handleVerificationAcceptAll = async () => {
    if (!fullReturnAnalysis?.analysisId) return;
    dispatch(setVerificationLoading(true));
    try {
      const res = await documentAnalysisApi.verifyReturn(
        selectedTaxYear, fullReturnAnalysis.analysisId, [], true
      );
      dispatch(setFullReturnAnalysis(res.data));
    } catch {
      // Verification failed silently — user can retry
    } finally {
      dispatch(setVerificationLoading(false));
    }
  };

  const handleVerificationSubmit = async (corrections: Array<{
    doc_index: number;
    field_name: string;
    extracted_value: number;
    corrected_value: number | null;
  }>) => {
    if (!fullReturnAnalysis?.analysisId) return;
    dispatch(setVerificationLoading(true));
    try {
      const res = await documentAnalysisApi.verifyReturn(
        selectedTaxYear, fullReturnAnalysis.analysisId, corrections
      );
      dispatch(setFullReturnAnalysis(res.data));
    } catch {
      // Verification failed silently
    } finally {
      dispatch(setVerificationLoading(false));
    }
  };

  const handleVerificationSkip = () => {
    // User chose to skip — don't change verification status
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const uploadedDocumentTypes = new Set(documents.map((doc) => doc.documentType?.toUpperCase()).filter(Boolean));
  const verificationFields = fullReturnAnalysis?.extractedFields || [];
  const lowConfidenceThreshold = 0.85;
  const lowConfidenceFields = verificationFields.filter(
    (field) => typeof field.confidence === 'number' && field.confidence < lowConfidenceThreshold
  );
  const documentAssignments = fullReturnAnalysis?.documentPersonAssignments || [];
  const isJointReturn = ['married-jointly', 'married_filing_jointly', 'married_filing_separately', 'married-separately'].includes(
    questionnaireAnswers.filingStatus || ''
  );
  const residenceState = (questionnaireAnswers.personalInfo?.stateOfResidence || '').trim().toUpperCase();
  const noIncomeTaxStates = new Set(['AK', 'FL', 'NV', 'SD', 'TN', 'TX', 'WA', 'WY', 'NH']);
  const stateFilingLikelyNeeded = !!residenceState && !noIncomeTaxStates.has(residenceState);

  const hasAnyDocumentType = (candidates: string[]) =>
    candidates.some((candidate) => uploadedDocumentTypes.has(candidate.toUpperCase()));

  const missingExpectedDocs = [
    (questionnaireAnswers.incomeSources || []).includes('w2') && !hasAnyDocumentType(['W-2'])
      ? 'You marked W-2 income, but no W-2 is currently uploaded.'
      : null,
    (questionnaireAnswers.incomeSources || []).includes('1099') &&
    !hasAnyDocumentType(['1099-NEC', '1099-MISC', 'SCHEDULE C', 'K-1'])
      ? 'You marked self-employment or 1099 income, but Filie does not see a matching 1099-NEC, 1099-MISC, Schedule C, or K-1 yet.'
      : null,
    (questionnaireAnswers.incomeSources || []).includes('investment') &&
    !hasAnyDocumentType(['1099-INT', '1099-DIV', '1099-B', 'K-1'])
      ? 'You marked investment income, but no 1099-INT, 1099-DIV, 1099-B, or K-1 is currently uploaded.'
      : null,
    (questionnaireAnswers.incomeSources || []).includes('retirement') &&
    !hasAnyDocumentType(['1099-R', '1099-SSA'])
      ? 'You marked retirement income, but no 1099-R or SSA-1099 is currently uploaded.'
      : null,
    (questionnaireAnswers.deductionDetails?.itemized?.mortgageInterest || 0) > 0 &&
    !hasAnyDocumentType(['1098'])
      ? 'You entered mortgage interest, but no Form 1098 is currently uploaded.'
      : null,
  ].filter(Boolean) as string[];

  if (documents.length === 0) {
    return (
      <Layout>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Review</h1>
          <p className="text-gray-600">
            Please upload your tax documents first to generate your tax review.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
          <Info className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-blue-900 mb-2">No Documents Uploaded</h3>
          <p className="text-blue-800 mb-6">
            To generate your tax review, start by uploading your W-2s, 1099s, and other tax documents.
          </p>
          <button
            onClick={() => navigate('/upload')}
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            Go to Upload Documents
          </button>
        </div>
      </Layout>
    );
  }

  if (analysisLoading) {
    return (
      <Layout>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Review</h1>
          <p className="text-gray-600">
            Analyzing your complete tax return...
          </p>
        </div>

        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-2">
              Filie is analyzing your complete return...
            </p>
            <p className="text-gray-600">
              This may take a moment as we review all your documents and calculate your tax liability.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (analysisError || !fullReturnAnalysis) {
    return (
      <Layout>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Review</h1>
          <p className="text-gray-600">
            Review your estimated tax information below.
          </p>
        </div>

        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Analysis Error</p>
            <p className="text-red-600 text-sm">{analysisError || 'Unable to load analysis. Please try again.'}</p>
            <button
              onClick={handleRefreshAnalysis}
              className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              Retry Analysis
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your {selectedTaxYear} Tax Return Summary</h1>
          <p className="text-gray-600">
            Your complete tax analysis based on uploaded documents.
          </p>
        </div>
        <button
          onClick={handleRefreshAnalysis}
          disabled={analysisLoading}
          className="px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Verification Status Badge */}
      {fullReturnAnalysis.verificationStatus && fullReturnAnalysis.verificationStatus !== 'unverified' && (
        <div className="mb-4 flex items-center space-x-2 text-sm">
          <ShieldCheck className="w-4 h-4 text-green-600" />
          <span className="text-green-700 font-medium">
            {fullReturnAnalysis.verificationStatus === 'verified' ? 'Values verified by you' : 'Values corrected and recalculated'}
          </span>
        </div>
      )}

      <div className={`mb-6 rounded-xl border p-4 ${
        stateFilingLikelyNeeded ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'
      }`}>
        <div className="flex items-start gap-3">
          <Info className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
            stateFilingLikelyNeeded ? 'text-amber-600' : 'text-blue-600'
          }`} />
          <div>
            <p className={`text-sm font-medium ${
              stateFilingLikelyNeeded ? 'text-amber-900' : 'text-blue-900'
            }`}>
              Federal return analysis only for now
            </p>
            <p className={`mt-1 text-sm ${
              stateFilingLikelyNeeded ? 'text-amber-800' : 'text-blue-800'
            }`}>
              {stateFilingLikelyNeeded
                ? `Your review covers federal taxes only. Because you listed ${residenceState}, you may still need a separate state return workflow after this federal review.`
                : 'Your review covers federal taxes only right now. We are using beta feedback to decide which state-return support should come first.'}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <p className="text-sm font-semibold text-blue-900">How to read this review</p>
          <div className="mt-3 space-y-2 text-sm text-blue-800">
            <div className="flex flex-wrap items-center gap-2">
              <TrustFilieBadge source="document" />
              <span>Values read from uploaded forms</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TrustFilieBadge source="user" />
              <span>Values you entered or corrected</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TrustFilieBadge source="trust-filie" />
              <span>Filie recommendations and calculations</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-semibold text-amber-900">Needs your attention</p>
          <div className="mt-3 space-y-2 text-sm text-amber-800">
            <p>{verificationFields.length} extracted {verificationFields.length === 1 ? 'value' : 'values'} are available to verify.</p>
            <p>{lowConfidenceFields.length > 0 ? `${lowConfidenceFields.length} values were read with lower confidence.` : 'No low-confidence document reads are currently flagged.'}</p>
            <p>{missingExpectedDocs.length > 0 ? `${missingExpectedDocs.length} expected document checks are still open.` : 'Your uploaded documents match the income and deduction signals Filie expects so far.'}</p>
          </div>
        </div>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <p className="text-sm font-semibold text-emerald-900">What Filie already checked</p>
          <div className="mt-3 space-y-2 text-sm text-emerald-800">
            <p>{documents.length} document{documents.length !== 1 ? 's' : ''} included in this review.</p>
            <p>{fullReturnAnalysis.riskFlags.length} active alert{fullReturnAnalysis.riskFlags.length !== 1 ? 's' : ''} surfaced in the return.</p>
            <p>{forms.length > 0 ? `${forms.length} generated tax form${forms.length !== 1 ? 's' : ''} ready to download.` : 'Forms can be generated once you are comfortable with the review.'}</p>
          </div>
        </div>
      </div>

      {(lowConfidenceFields.length > 0 || missingExpectedDocs.length > 0) && (
        <div className="mb-8 rounded-xl border border-amber-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900">Before you rely on this result</h2>
              <div className="mt-3 space-y-2 text-sm text-gray-700">
                {lowConfidenceFields.slice(0, 4).map((field, idx) => (
                  <div key={`${field.docIndex}_${field.fieldName}_${idx}`} className="rounded-lg bg-amber-50 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <TrustFilieBadge source="document" documentType={field.sourceDocumentType} />
                      <span className="font-medium text-gray-900">{field.displayLabel}</span>
                      <span className="text-amber-700">{Math.round((field.confidence || 0) * 100)}% confidence</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      {field.sourceName ? `${field.sourceName} · ` : ''}
                      {field.recipientName ? `Recipient: ${field.recipientName} · ` : ''}
                      Value read: {formatCurrency(field.extractedValue)}
                    </p>
                  </div>
                ))}
                {missingExpectedDocs.map((message, idx) => (
                  <div key={idx} className="rounded-lg bg-red-50 px-3 py-2 text-red-800">
                    {message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isJointReturn && documentAssignments.length > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <Users className="h-5 w-5 text-gray-700" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Document assignment</h2>
              <p className="text-sm text-gray-500">Review which uploaded documents Filie grouped under each person.</p>
            </div>
          </div>
          <div className="divide-y divide-gray-100 px-6 py-2">
            {documentAssignments.map((assignment, idx) => (
              <div key={`${assignment.docIndex}_${idx}`} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <TrustFilieBadge source="document" documentType={assignment.docType} />
                    <span className="font-medium text-gray-900">{assignment.docType}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Document #{assignment.docIndex + 1}
                  </p>
                </div>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
                  {assignment.person}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Panel — shown when unverified and extractedFields available */}
      {fullReturnAnalysis.verificationStatus === 'unverified' &&
        fullReturnAnalysis.extractedFields &&
        fullReturnAnalysis.extractedFields.length > 0 && (
        <VerificationPanel
          extractedFields={fullReturnAnalysis.extractedFields}
          onAcceptAll={handleVerificationAcceptAll}
          onSubmitCorrections={handleVerificationSubmit}
          onSkip={handleVerificationSkip}
          loading={verificationLoading}
        />
      )}

      {/* Disclaimer - Prominent Position */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-900 mb-2">Important Disclaimer</p>
          <p className="text-sm text-amber-800">
            {fullReturnAnalysis.disclaimerText || 'This analysis is AI-generated and provided for informational purposes only. It contains tax planning considerations, not professional tax advice. Review all amounts for accuracy and consult with a qualified tax professional before filing.'}
          </p>
        </div>
      </div>

      {/* Year-Over-Year Comparison */}
      {fullReturnAnalysis.yearOverYearComparison && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Year-Over-Year Comparison</h2>
              <TrustFilieBadge source="trust-filie" />
            </div>
          </div>
          <div className="p-6">
            {fullReturnAnalysis.yearOverYearComparison.incomeChangePct !== null && (
              <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-3">
                <span className="text-gray-700">Income Change</span>
                <span className={`font-bold ${fullReturnAnalysis.yearOverYearComparison.incomeChangePct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {fullReturnAnalysis.yearOverYearComparison.incomeChangePct >= 0 ? '+' : ''}{fullReturnAnalysis.yearOverYearComparison.incomeChangePct}%
                </span>
              </div>
            )}
            {fullReturnAnalysis.yearOverYearComparison.notableChanges && fullReturnAnalysis.yearOverYearComparison.notableChanges.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Notable Changes</p>
                <ul className="space-y-1">
                  {fullReturnAnalysis.yearOverYearComparison.notableChanges.map((change: string, idx: number) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">&#8226;</span>
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fullReturnAnalysis.yearOverYearComparison.estimatedPaymentAdequacy && (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-900">Estimated Payment Status</p>
                <p className="text-sm text-blue-800">{fullReturnAnalysis.yearOverYearComparison.estimatedPaymentAdequacy}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Income Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Income Summary</h2>
              <TrustFilieBadge source="document" />
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3 mb-6">
              {fullReturnAnalysis.incomeSummary.lineItems.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center border-b border-gray-100 pb-3">
                  <span className="text-gray-700">{item.label}</span>
                  <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total Income</span>
              <span className="text-xl font-bold text-blue-600">
                {formatCurrency(fullReturnAnalysis.incomeSummary.totalIncome)}
              </span>
            </div>
          </div>
        </div>

        {/* Deductions Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Deductions Analysis</h2>
              <TrustFilieBadge source="trust-filie" />
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-600 mb-2">Recommendation</div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900 font-semibold mb-4">
                  {fullReturnAnalysis.deductionAnalysis.recommendation === 'standard' ? 'Standard' : 'Itemized'} Deduction
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Standard Deduction</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(fullReturnAnalysis.deductionAnalysis.standardDeduction)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Itemized Deductions</p>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(fullReturnAnalysis.deductionAnalysis.itemizedDeductions)}</p>
              </div>
              {fullReturnAnalysis.deductionAnalysis.potentialSavings > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium">Potential Savings</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(fullReturnAnalysis.deductionAnalysis.potentialSavings)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Credits Section */}
      {fullReturnAnalysis.creditsIdentified.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-green-50 to-green-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Credits Identified</h2>
              <TrustFilieBadge source="trust-filie" />
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {fullReturnAnalysis.creditsIdentified.map((credit, idx) => (
                <div key={idx} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{credit.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{credit.reason}</p>
                  </div>
                  <span className="text-lg font-bold text-green-600 ml-4">{formatCurrency(credit.estimatedValue)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tax Calculation Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-bold text-gray-900">Tax Calculation Breakdown</h2>
            <TrustFilieBadge source="trust-filie" />
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Gross Income</span>
              <span className="font-medium text-gray-900">{formatCurrency(fullReturnAnalysis.taxCalculation.grossIncome)}</span>
            </div>

            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Adjusted Gross Income (AGI)</span>
              <span className="font-medium text-gray-900">{formatCurrency(fullReturnAnalysis.taxCalculation.adjustedGrossIncome)}</span>
            </div>

            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Taxable Income</span>
              <span className="font-medium text-gray-900">{formatCurrency(fullReturnAnalysis.taxCalculation.taxableIncome)}</span>
            </div>

            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Estimated Federal Tax</span>
              <span className="font-medium text-gray-900">{formatCurrency(fullReturnAnalysis.taxCalculation.estimatedTax)}</span>
            </div>

            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Tax Credits</span>
              <span className="font-medium text-gray-900 text-green-600">-{formatCurrency(fullReturnAnalysis.taxCalculation.credits)}</span>
            </div>

            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
              <span className="text-gray-700">Total Federal Taxes Withheld</span>
              <span className="font-medium text-gray-900">{formatCurrency(fullReturnAnalysis.taxCalculation.taxWithheld)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filing Comparison (MFJ vs MFS) */}
      {fullReturnAnalysis.filingComparison && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Filing Status Analysis: Joint vs Separate</h2>
              <TrustFilieBadge source="trust-filie" />
            </div>
          </div>
          <div className="p-6">
            {(() => {
              const fc = fullReturnAnalysis.filingComparison;
              const isJointBetter = fc.savings > 0;
              const isSeparateBetter = fc.savings < 0;
              return (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className={`p-4 rounded-lg border-2 ${isJointBetter ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <p className="text-sm font-medium text-gray-600 mb-1">Married Filing Jointly</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(fc.mfjTax)}</p>
                      <p className={`text-sm mt-1 ${fc.mfjRefund >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fc.mfjRefund >= 0 ? `Refund: ${formatCurrency(fc.mfjRefund)}` : `Owe: ${formatCurrency(Math.abs(fc.mfjRefund))}`}
                      </p>
                      {isJointBetter && <span className="inline-block mt-2 px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded">Recommended</span>}
                    </div>
                    <div className={`p-4 rounded-lg border-2 ${isSeparateBetter ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <p className="text-sm font-medium text-gray-600 mb-1">Married Filing Separately</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(fc.mfsTax)}</p>
                      <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                        <p>{fc.mfsBreakdown.person1.name}: {formatCurrency(fc.mfsBreakdown.person1.tax)}</p>
                        <p>{fc.mfsBreakdown.person2.name}: {formatCurrency(fc.mfsBreakdown.person2.tax)}</p>
                      </div>
                      {isSeparateBetter && <span className="inline-block mt-2 px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded">Recommended</span>}
                    </div>
                  </div>
                  <div className={`rounded-lg p-4 ${isJointBetter || isSeparateBetter ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className={`font-medium ${isJointBetter || isSeparateBetter ? 'text-green-800' : 'text-gray-800'}`}>
                      {fc.recommendationText}
                    </p>
                  </div>
                  {fc.mfsDisqualifiedCredits && fc.mfsDisqualifiedCredits.length > 0 && (
                    <p className="text-xs text-gray-500 mt-3">
                      Note: Filing separately would disqualify you from: {fc.mfsDisqualifiedCredits.join(', ')}.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Refund/Amount Due */}
      <div className={`rounded-xl p-8 mb-8 flex items-start space-x-4 border ${
        fullReturnAnalysis.taxCalculation.refundOrDue >= 0
          ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
          : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
      }`}>
        <CheckCircle className={`w-8 h-8 flex-shrink-0 mt-1 ${
          fullReturnAnalysis.taxCalculation.refundOrDue >= 0 ? 'text-green-600' : 'text-red-600'
        }`} />
        <div className="flex-1">
          <p className={`text-sm font-medium uppercase tracking-wide ${
            fullReturnAnalysis.taxCalculation.refundOrDue >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {fullReturnAnalysis.taxCalculation.refundOrDue >= 0 ? 'Expected Refund' : 'Amount Due'}
          </p>
          <p className={`text-5xl font-bold mt-2 ${
            fullReturnAnalysis.taxCalculation.refundOrDue >= 0 ? 'text-green-700' : 'text-red-700'
          }`}>
            {formatCurrency(Math.abs(fullReturnAnalysis.taxCalculation.refundOrDue))}
          </p>
          <p className={`text-sm mt-2 ${
            fullReturnAnalysis.taxCalculation.refundOrDue >= 0 ? 'text-green-700' : 'text-red-700'
          }`}>
            {fullReturnAnalysis.taxCalculation.refundOrDue >= 0
              ? 'Based on your tax withholding and deductions, you should receive this amount as a refund.'
              : 'You will owe this amount in taxes. Plan ahead for payment.'}
          </p>
        </div>
      </div>

      {/* Tax Savings Opportunities */}
      {fullReturnAnalysis.savingsOpportunities && fullReturnAnalysis.savingsOpportunities.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-bold text-gray-900">Tax Savings Opportunities</h2>
            <p className="text-xs text-gray-500 mt-0.5">Based on your return, you may be missing these deductions or credits</p>
          </div>
          <div className="divide-y divide-gray-100">
            {fullReturnAnalysis.savingsOpportunities.map((opp, idx) => (
              <div key={idx} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                        opp.priority === 'high' ? 'bg-red-100 text-red-700' :
                        opp.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{opp.priority}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-gray-100 text-gray-500">
                        {opp.category}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">{opp.title}</h3>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{opp.description}</p>
                  </div>
                  {opp.estimatedSavings > 0 && (
                    <div className="ml-4 text-right flex-shrink-0">
                      <p className="text-xs text-gray-400">Est. savings</p>
                      <p className="text-sm font-bold text-green-600">{formatCurrency(opp.estimatedSavings)}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Narrative Summary */}
      {fullReturnAnalysis.narrativeSummary && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Your Tax Return, Explained Clearly</h2>
              <TrustFilieBadge source="trust-filie" />
            </div>
          </div>
          <div className="p-6">
            <div
              className="prose prose-gray prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: fullReturnAnalysis.narrativeSummary
                  .replace(/^# .+$/gm, '')
                  .replace(/^## (.+)$/gm, '<h3 class="text-base font-semibold text-gray-900 mt-5 mb-2">$1</h3>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n\n/g, '</p><p class="text-gray-700 leading-relaxed mb-3">')
                  .replace(/^\|(.+)$/gm, (match: string) => {
                    if (match.includes('---')) return '';
                    const cells = match.split('|').filter(Boolean).map((c: string) => c.trim());
                    const tag = match.includes('Bracket') || match.includes('Rate') ? 'th' : 'td';
                    return `<tr>${cells.map((c: string) => `<${tag} class="px-3 py-1.5 text-sm border border-gray-200">${c}</${tag}>`).join('')}</tr>`;
                  })
                  .replace(/(<tr>.*<\/tr>\n?)+/g, (m: string) => `<table class="w-full border-collapse my-3">${m}</table>`)
                  .replace(/^- (.+)$/gm, '<li class="text-gray-700 text-sm ml-4">$1</li>')
                  .replace(/(<li.*<\/li>\n?)+/g, (m: string) => `<ul class="list-disc mb-3">${m}</ul>`)
              }}
            />
          </div>
        </div>
      )}

      {/* Risk Flags */}
      {fullReturnAnalysis.riskFlags.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Important Alerts</h2>
          <div className="space-y-3">
            {fullReturnAnalysis.riskFlags.map((flag, idx) => (
              <div
                key={idx}
                className={`rounded-xl p-4 flex items-start space-x-3 border ${
                  flag.severity === 'error'
                    ? 'bg-red-50 border-red-200'
                    : flag.severity === 'warning'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  flag.severity === 'error'
                    ? 'text-red-600'
                    : flag.severity === 'warning'
                    ? 'text-yellow-600'
                    : 'text-blue-600'
                }`} />
                <p className={`${
                  flag.severity === 'error'
                    ? 'text-red-800'
                    : flag.severity === 'warning'
                    ? 'text-yellow-800'
                    : 'text-blue-800'
                }`}>
                  {flag.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tax Suggestions */}
      {taxSuggestions && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Smart Suggestions</h2>
          <TaxSuggestionsPanel suggestions={taxSuggestions} />
        </div>
      )}

      {/* Tax Planning Considerations */}
      {fullReturnAnalysis.taxPlanningConsiderations && fullReturnAnalysis.taxPlanningConsiderations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Tax Planning Considerations</h2>
          <p className="text-sm text-gray-500 mb-4">These are AI-generated considerations for your tax planning, not professional advice.</p>
          <div className="space-y-3">
            {fullReturnAnalysis.taxPlanningConsiderations.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium capitalize">{item.category?.replace(/_/g, ' ')}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.timeframe === 'current_year' ? 'bg-green-100 text-green-800' :
                      item.timeframe === 'next_year' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>{item.timeframe?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <p className="text-gray-900 font-medium mb-1">{item.consideration}</p>
                {item.potential_impact && (
                  <p className="text-sm text-gray-600">Potential impact: {item.potential_impact}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generated Tax Forms */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-bold text-gray-900">Generated Tax Forms</h2>
            <TrustFilieBadge source="trust-filie" />
          </div>
          <button
            onClick={async () => {
              try {
                dispatch(setFormsLoading(true));
                await taxFormsApi.generateForms(selectedTaxYear);
                const res = await taxFormsApi.getLatestForms(selectedTaxYear);
                dispatch(setForms(res.data.forms || []));
              } catch {
                // Handle error silently
              } finally {
                dispatch(setFormsLoading(false));
              }
            }}
            disabled={formsGenerating}
            className="px-3 py-1.5 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 transition-colors flex items-center space-x-1"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${formsGenerating ? 'animate-spin' : ''}`} />
            <span>{formsGenerating ? 'Generating...' : 'Regenerate'}</span>
          </button>
        </div>
        <div className="p-6">
          {forms.length === 0 ? (
            <p className="text-gray-500 text-sm">No forms generated yet. Forms are auto-generated when analysis runs, or click Regenerate above.</p>
          ) : (
            <div className="space-y-3">
              {forms.map((form) => (
                <div key={form.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-gray-900">{form.formType.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-gray-500">Version {form.version} — {form.generatedAt ? new Date(form.generatedAt).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await taxFormsApi.downloadForm(form.id);
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = form.fileName;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      } catch {
                        // Download failed
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => navigate('/upload')}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
        >
          <span>Upload More Documents</span>
        </button>

        <button
          className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 cursor-not-allowed opacity-50"
          disabled
          title="Feature coming soon"
        >
          <Send className="w-5 h-5" />
          <span>E-File Return</span>
        </button>
      </div>
    </Layout>
  );
}
